import React, { useContext } from 'react'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { ModalElementContext } from 'contexts/ModalProvider'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import moment from 'moment'
import { DAY_MONTH_NAME_YEAR } from 'constants/dateFormats'
import { getRateOptionByRateType } from 'lib/carHire/carHireUtils'
import { FormattedMessage, useIntl } from 'react-intl'

const PayAtPickupSummary = styled(Group)`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  padding: ${rem(12)} ${rem(16)};
`

interface Props {
  offer: App.CarHireOffer
  pickUpDate?: string
  onReserve: (offer: App.CarHireOffer) => void
}

function CarHirePriceDetailsModal(props: Props) {
  const { offer, pickUpDate, onReserve } = props
  const modalContext = useContext(ModalElementContext)
  const geo = useContext(GeoContext)
  const intl = useIntl()

  const onClick = () => {
    onReserve(offer)
    modalContext.resolve()
  }

  const payOnArrivalItemsString = arrayToStringListFormat(offer.payOnArrivalItems, 'conjunction', intl)
  const rateOption =
    getRateOptionByRateType(offer, 'payFullyOnArrival') ||
    getRateOptionByRateType(offer, 'payPartiallyNow') ||
    offer.rateOptions[0]

  const priceDetailsTitle = intl.formatMessage({
    id: 'MBvgWH3FXH',
    defaultMessage: 'Price details',
    description: 'Title for car hire price details modal',
  })

  const carHireDurationLabel = intl.formatMessage(
    {
      id: 'BoW9SVStD6',
      defaultMessage: 'Car Hire ({count, plural, one {# day} other {# days}})',
      description: 'Car hire line item label with rental duration in price details modal',
    },
    { count: offer.duration },
  )

  const taxesAndFeesLabel = intl.formatMessage({
    id: '2mzkZgy+EP',
    defaultMessage: 'Taxes & fees',
    description: 'Label for taxes and fees section in car hire price details modal',
  })

  const dueAtPickupLabel = intl.formatMessage({
    id: '9R1D9/ApEW',
    defaultMessage: 'Due at pick-up',
    description: 'Label indicating amount is due at pick-up in car hire price details',
  })

  const readMoreLabel = intl.formatMessage({
    id: 'KILdcl54fY',
    defaultMessage: 'Read more',
    description: 'Toggle text to expand pay-on-arrival details in car hire price details',
  })

  const readLessLabel = intl.formatMessage({
    id: 'AltkoayMxM',
    defaultMessage: 'Read less',
    description: 'Toggle text to collapse pay-on-arrival details in car hire price details',
  })

  const noItemsFeeText = intl.formatMessage({
    id: 'hgVWjaWJHH',
    defaultMessage: 'This charge covers estimated supplier fees. Please note that this fee will be payable directly at pick-up.',
    description: 'Explanation of pay-on-arrival charge when no specific items are listed',
  })

  const totalLabel = intl.formatMessage({
    id: 'fNUQer9YEB',
    defaultMessage: 'Total',
    description: 'Total price label in car hire price details modal',
  })

  const bookNowPayLaterLabel = intl.formatMessage({
    id: 'hh/+BNIFnA',
    defaultMessage: 'Book now and pay at pick-up',
    description: 'Heading for pay at pick-up summary when full amount is due at pick-up',
  })

  const payNowAndRestLaterLabel = intl.formatMessage(
    {
      id: 'gBULgxmCUs',
      defaultMessage: 'Pay {amount} now and the rest later',
      description: 'Heading for pay at pick-up summary when partial amount is paid now',
    },
    { amount: formatCurrency(rateOption.payNowAmount, geo.currentCurrency, geo.currentRegionCode) },
  )

  const bookNowLabel = intl.formatMessage({
    id: 'QEou7NGqRr',
    defaultMessage: 'Book now',
    description: 'Button text to proceed with booking in car hire price details modal',
  })

  const payAtPickupHeading = !rateOption.payNowAmount ? bookNowPayLaterLabel : payNowAndRestLaterLabel

  const pickUpDateFormatted = pickUpDate ? moment(pickUpDate).format(DAY_MONTH_NAME_YEAR) : ''
  const hasDateValue = pickUpDate ? 'true' : 'false'

  const remainingBalanceMessage = <FormattedMessage
    id="Hr+/ibKnUV"
    defaultMessage="The remaining balance of <bold>{price}</bold> will be charged at pick-up{hasDate, select, true { on {date}} other {}}."
    description="Remaining balance due at pick-up with optional date when partial payment is made now"
    values={{
      price: <FormatCurrency value={rateOption.payOnArrivalAmount} />,
      bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
      hasDate: hasDateValue,
      date: pickUpDateFormatted,
    }}
  />

  const totalAtPickupMessage = <FormattedMessage
    id="uJQaJDksuo"
    defaultMessage="The total of <bold>{price}</bold> will be charged at pick-up{hasDate, select, true { on {date}} other {}}."
    description="Total amount due at pick-up with optional date when no upfront payment is required"
    values={{
      price: <FormatCurrency value={rateOption.payOnArrivalAmount} />,
      bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
      hasDate: hasDateValue,
      date: pickUpDateFormatted,
    }}
  />

  const payAtPickupAmountMessage = rateOption.payNowAmount ? remainingBalanceMessage : totalAtPickupMessage

  return (
    <Modal title={priceDetailsTitle} size="L">
      <Group gap={32} direction="vertical">
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="medium" weight="bold">{carHireDurationLabel}</BodyText>
            <BodyText variant="medium">
              <FormattedMessage
                id="hDoH1SQHF/"
                defaultMessage="{price} total"
                description="Total price amount for car hire line item in price details"
                values={{ price: <FormatCurrency value={rateOption.payNowAmount || rateOption.payOnArrivalAmount} /> }}
              />
            </BodyText>
          </Group>

          {!!rateOption.payNowAmount && !!rateOption.payOnArrivalAmount &&
            <>
              <Divider kind="primary" />
              <Group direction="vertical">
                <Group direction="horizontal" horizontalAlign="space-between">
                  <BodyText variant="medium" weight="bold">{taxesAndFeesLabel}</BodyText>
                  <BodyText variant="medium"><FormatCurrency value={rateOption.payOnArrivalAmount} /></BodyText>
                </Group>
                <div>
                  <BodyText variant="medium">{dueAtPickupLabel}</BodyText>
                  <LargeContentToggle
                    maxHeight={0}
                    showMoreText={readMoreLabel}
                    showLessText={readLessLabel}
                  >
                    {offer.payOnArrivalItems.length > 0 &&
                      <BodyText variant="medium">
                        <FormattedMessage
                          id="5zKUKmE2z/"
                          defaultMessage="This charge includes estimated amounts the supplier requires for <bold>{items}</bold>. Please note that this fee will be payable directly at pick-up."
                          description="Explanation of pay-on-arrival charge listing specific fee items"
                          values={{
                            items: payOnArrivalItemsString,
                            bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
                          }}
                        />
                      </BodyText>}

                    {offer.payOnArrivalItems.length <= 0 && <BodyText variant="medium">
                      {noItemsFeeText}
                    </BodyText>}
                  </LargeContentToggle>
                </div>
              </Group>
            </>}

          <Divider kind="primary" />
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="large" weight="bold">{totalLabel}</BodyText>
            <BodyText variant="large" weight="bold"><FormatCurrency value={offer.price} /></BodyText>
          </Group>

          {!!rateOption.payOnArrivalAmount &&
            <PayAtPickupSummary direction="horizontal" gap={8}>
              <LineInfoCircleIcon />
              <VerticalSpacer gap={4}>
                <BodyText variant="medium" weight="bold">
                  {payAtPickupHeading}
                </BodyText>
                <BodyText variant="medium">
                  {payAtPickupAmountMessage}
                </BodyText>
              </VerticalSpacer>
            </PayAtPickupSummary>}
        </VerticalSpacer>

        <Group direction="horizontal" tabletHorizontalAlign="end">
          <TextButton kind="primary" onClick={onClick}>{bookNowLabel}</TextButton>
        </Group>

      </Group>

    </Modal>
  )
}

export default CarHirePriceDetailsModal
